/* eslint-disable no-loop-func */
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Box, Grid, InternalStandardProps, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AppHeader } from "JS/React/Components/AppHeader";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { MediaPlayer } from "JS/React/Components/MediaPlayer";
import { GraphicEq as GraphicEqIcon } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { OfflineBundle } from "JS/Models/Common";
import {
    Content,
    EventActions,
    EventCategories,
    EventNames,
    FirebaseEventAction,
    ReceivedGift,
    ResumeContent,
    contentTypeOf,
} from "JS/Models";
import { useGlobalAudios } from "JS/React/Hooks/Audio";
import {
    useGlobalGifts,
    useReceivedMemberAudioGifts,
} from "JS/React/Hooks/Gifts";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import { useOwnedBlobUrl } from "JS/React/Hooks/Media";
import qs from "qs";
import moment from "moment";
import {
    asyncSortArrayByNumberKey,
    setPlayedTime,
    sortArrayByKey,
} from "JS/Helpers";
import { getDexieConnectionRefresh } from "JS/Database/Dexie";
import FallbackWaveform from "Images/Content/audio-waves.png";
import { IndexableTypeArray, IndexableTypePart } from "dexie";
import {
    useContentPlayingHandlers,
    usePlayerHandlers,
} from "JS/React/Hooks/MediaPlayer";
import { useMiniPlayerHandlers } from "JS/React/Hooks/MiniPlayer";
import {
    usePublishDisclosurePlaying,
    useDisclosureGate,
    useDisclosurePlaying,
} from "JS/React/Hooks/Disclosure";
import { useIsResumingBundle } from "JS/React/Hooks/ResumeRecently";
import { useDisclosureSequence } from "JS/React/Hooks/DisclosureSequence";
import {
    audiobookPartDisclosure,
    useIsAudiobook,
} from "JS/React/Hooks/Audiobook";
import { PlayerActionBtns } from "JS/React/Components/MediaPlayer/PlayerActionBtns";
import { PLAYER_PROGRESS_INTERVAL } from "JS/Helpers/Constants";
import {
    SentGiftType,
    getPlayerPlayedTimeForBundle,
    toSentOfflineGift,
} from "JS/Models/Firebase/GiftDashboard";
import { useGiftDashboard } from "JS/React/Hooks/Firebase/GiftDashboard";

const RootDiv = styled("div")(({ theme }) => ({
    padding: theme.spacing(2),
}));

const Grey800Typography = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[800],
}));

const PlayerContainerGrid = styled(Grid)(({ theme }) => ({
    margin: theme.spacing(2, 0),
    height: "60vh",
    [theme.breakpoints.down("lg")]: {
        height: "50vh",
    },
    [theme.breakpoints.down("sm")]: {
        height: "30vh",
    },
}));

const GraphicIcon = styled(GraphicEqIcon)(({ theme }) => ({
    color: theme.palette.grey[400],
}));

const GraphicWrapper = styled("span")(({ theme }) => ({
    border: `5px solid ${theme.palette.common.white}`,
    borderRadius: "50%",
    height: "120px",
    width: "120px",
    marginRight: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
}));

const DummyImageBox = styled(Box)(({ theme }) => ({
    position: "relative",
    background: theme.palette.grey[900],
    height: "100%",
}));

const ForwardBackItemBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    color: theme.palette.grey.A100,
    cursor: "pointer",
}));

const FwdBackTypography = styled(Typography)({
    fontSize: "18px",
});

const FwdBackIcon = styled(FontAwesomeIcon)({
    fontSize: "18px",
});

const BackIcon = styled(FwdBackIcon)(({ theme }) => ({
    marginRight: theme.spacing(2),
}));

const FwdIcon = styled(FwdBackIcon)(({ theme }) => ({
    marginLeft: theme.spacing(2),
}));

export interface BundlePlayerOfflineProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export const BundlePlayerOffline = (props: BundlePlayerOfflineProps) => {
    const { className, ...rest } = props;
    const params: any = useParams();
    const { contentId } = params;
    const [mediaURL, setMediaURL] = useState("");
    const [audio, setAudio] = useState<OfflineBundle>();

    const location = useLocation();

    const bundlePlayerRef = useRef<any>(null);
    const { handleChangeSeek } = usePlayerHandlers(bundlePlayerRef);
    const [isReady, setIsReady] = useState<boolean>(false);
    const [isReadyToPlay, setIsReadyToPlay] = useState<boolean>(false);
    const [currentPlayingState, setCurrentPlayingState] = useState(true);
    const { handlePlayerPause, handlePlayerPlay } = useContentPlayingHandlers(
        setCurrentPlayingState,
    );
    const { miniPlayer } = useMiniPlayerHandlers();
    const isDisclosurePlaying = useDisclosurePlaying();

    const queryParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        const { index = 0 } = parsedQueryString;

        return {
            index: index.toString(),
        };
    }, [location?.search]);

    const mediaIndex = +queryParams.index;
    const navigate = useNavigate();
    const setQueryParams = (params: Partial<typeof queryParams>) => {
        navigate(
            {
                pathname: location.pathname,
                search: qs.stringify({ ...queryParams, ...params }),
            },
            { replace: true },
        );
    };

    const {
        audios,
        resumeUserBundles,
        setGlobalAudios,
        updateRecentlyPlayed,
        updateResumeBundles,
    } = useGlobalAudios();
    const { receivedMemberAudios, receivedProspectAudios, setGlobalGifts } =
        useGlobalGifts();
    const media = audio?.media;
    const isBundle = media && media?.length > 1;

    const downloadedAudioDetail = useMemo(() => {
        const findFrom: (ReceivedGift | Content)[] = [];
        if (audios) findFrom.push(...audios);
        if (receivedMemberAudios) findFrom.push(...receivedMemberAudios);
        if (receivedProspectAudios) findFrom.push(...receivedProspectAudios);

        return [...findFrom]?.find((x) => x.sku_id === audio?.skuId);
    }, [audios, audio]);

    const giftDashboardData = useMemo(() => {
        return toSentOfflineGift(audio, mediaIndex);
    }, [audio, mediaIndex]);

    const getDashboardActionDetails = () => {
        return getPlayerPlayedTimeForBundle(bundlePlayerRef, mediaIndex + 1);
    };
    const { isGift } = useReceivedMemberAudioGifts(true);
    const isReceivedMemberAudio = isGift(contentId.split("-")[1]);

    const { logGiftCompleted } = useGiftDashboard(
        giftDashboardData,
        SentGiftType.AUDIO,
        (downloadedAudioDetail as ReceivedGift)?.sender?.member_id,
        isReceivedMemberAudio && currentPlayingState && !miniPlayer.url,
        getDashboardActionDetails,
        true,
    );

    const next = () => {
        setIsReady(false);
        setIsReadyToPlay(false);
        setCurrentPlayingState(true);
        setQueryParams({
            index: ((mediaIndex + 1) % media?.length).toString(),
        });
    };

    const prepareBundle = async (contentId: string) => {
        const bundleCollection = getDexieConnectionRefresh().audios.filter(
            (audio) => audio.isBundle === true,
        );
        let availableBundlePartsNids: string[] = [];
        const splittedContentId = contentId.split("-");
        const id = `${splittedContentId[0]}-${splittedContentId[1]}`;
        let audio: OfflineBundle = {};

        await bundleCollection.keys().then((res) => {
            if (res && res.length) {
                let downloadedBundlePartsNids: IndexableTypeArray = res;
                downloadedBundlePartsNids.forEach((nid: IndexableTypePart) => {
                    if (nid?.toString()?.startsWith(id)) {
                        availableBundlePartsNids.push(nid?.toString());
                    }
                });
            }
        });

        for (let i = 0; i < availableBundlePartsNids.length; i++) {
            await getDexieConnectionRefresh()
                .audios.get(availableBundlePartsNids[i])
                .then(async (res) => {
                    if (res) {
                        if (Object.keys(audio).length) {
                            const mediaArray = Object.assign([], audio.media);
                            audio = {
                                ...audio,
                                media: [
                                    ...mediaArray,
                                    {
                                        media_title: res.media_title,
                                        position: res.position,
                                        blob: res.blob,
                                    },
                                ],
                            };
                        } else {
                            audio = {
                                id,
                                name: res.name,
                                media: [
                                    {
                                        media_title: res.media_title,
                                        position: res.position,
                                        blob: res.blob,
                                    },
                                ],
                                skuId: res.skuId,
                                description: res.description,
                                release_date: res.release_date,
                                disclosure: res.disclosure,
                            };
                        }
                    }
                });
            const sortedMedia = await asyncSortArrayByNumberKey(
                [...audio.media],
                "position",
                "ASC",
            );
            audio = {
                ...audio,
                media: sortedMedia,
            };
            if (i === availableBundlePartsNids.length - 1) {
                setAudio(audio);
            }
        }
    };
    useEffect(() => {
        if (contentId) {
            prepareBundle(contentId);
        }
    }, [contentId]);

    const previous = () => {
        setIsReady(false);
        setIsReadyToPlay(false);
        setCurrentPlayingState(true);
        setQueryParams({
            index: (mediaIndex === 0
                ? media?.length - 1
                : mediaIndex - 1
            ).toString(),
        });
    };
    const completeCurrentPart = () => {
        const data: ResumeContent = {
            nid: audio.id,
            sku_id: audio.skuId,
            title: media[mediaIndex].media_title,
            lastPlayed: bundlePlayerRef.current.getCurrentTime(),
            maximumPlayed: bundlePlayerRef.current.getCurrentTime(),
            lastCompletedTime: bundlePlayerRef.current.getCurrentTime(),
            duration: bundlePlayerRef.current.getDuration(),
        };
        if (isReceivedMemberAudio) {
            const actionsDetails = getDashboardActionDetails();
            logGiftCompleted({ index: actionsDetails.index });
        }
        updateResumeBundles(data);
    };

    const advanceToNextPart = () => {
        if (mediaIndex < media?.length - 1) next();
    };

    const onComplete = () => {
        completeCurrentPart();
        advanceToNextPart();
    };
    const { logFirebaseEvent } = useFirebaseLogger();

    useEffect(() => {
        if (media && media[mediaIndex]) {
            setMediaURL(URL.createObjectURL(media[mediaIndex]?.blob));
        }
    }, [audio?.id, media, mediaIndex]);

    useOwnedBlobUrl(mediaURL);

    useEffect(() => {
        if (!miniPlayer?.url && audio && isReady) {
            onMediaPlayerReady(true);
        }
    }, [miniPlayer]);

    const onMediaPlayerReady = useCallback(
        (must?: boolean) => {
            setIsReadyToPlay(true);
            if (isDisclosurePlaying) return;
            if ((!isReady || must) && audio && media[mediaIndex]?.media_title) {
                const nid = audio?.id?.split("-")[1]
                    ? audio?.id?.split("-")[1]
                    : "";

                const resumeData: ResumeContent = resumeUserBundles
                    ? resumeUserBundles.find(
                          (x) =>
                              x.nid === nid &&
                              x.title === media[mediaIndex]?.media_title,
                      )
                    : ({} as ResumeContent);
                if (
                    resumeData &&
                    Math.ceil(+resumeData.lastPlayed) !==
                        Math.ceil(+resumeData.duration)
                ) {
                    bundlePlayerRef.current.seekTo(
                        resumeData.lastPlayed,
                        "seconds",
                    );
                }

                const data: ResumeContent = {
                    nid: nid,
                    sku_id: audio.skuId,
                    title: media[mediaIndex]?.media_title,
                    lastPlayed: bundlePlayerRef.current.getCurrentTime(),
                    maximumPlayed: bundlePlayerRef.current.getCurrentTime(),
                    duration: bundlePlayerRef.current.getDuration(),
                };

                updateResumeBundles(data);
                if (!isReady) handleRecentlyPlayed();
                setIsReady(true);
            }
        },
        [
            isReady,
            audio?.id,
            media,
            mediaIndex,
            resumeUserBundles,
            isDisclosurePlaying,
        ],
    );

    const handleAudioStartedPlaying = () => {
        if (audio) {
            const { id, name: sku } = audio;
            const nid = id?.split("-")[1] ? id?.split("-")[1] : "";

            const action: FirebaseEventAction = {
                action: EventActions.PLAY,
                nId: nid,
                contentTitle: media[mediaIndex].media_title,
                skuId: sku,
                category: EventCategories.AUDIOS,
            };

            logFirebaseEvent(EventNames.AUDIO_PLAYED, action);
            const data: ResumeContent = {
                nid: nid,
                sku_id: audio.skuId,
                title: media[mediaIndex].media_title,
                lastPlayed: bundlePlayerRef.current.getCurrentTime(),
                maximumPlayed: bundlePlayerRef.current.getCurrentTime(),
                duration: bundlePlayerRef.current.getDuration(),
            };
            updateResumeBundles(data);
            handleRecentlyPlayed();
        }
    };

    const onPlayerProgress = async () => {
        if (isDisclosurePlaying) return;
        if (audio) {
            const nid = audio?.id?.split("-")[1]
                ? audio?.id?.split("-")[1]
                : "";
            const data: ResumeContent = {
                nid: nid,
                sku_id: audio.skuId,
                title: media[mediaIndex].media_title,
                lastPlayed: bundlePlayerRef.current.getCurrentTime(),
                maximumPlayed: bundlePlayerRef.current.getCurrentTime(),
            };
            updateResumeBundles(data);
        }
    };

    const handleRecentlyPlayed = () => {
        const timeStamp = moment().unix().toString();
        const nid = contentId?.split("-")[1] ? contentId?.split("-")[1] : "";
        let audio: Content | ReceivedGift;

        if (audios?.filter((a) => a.nid === nid).length > 0) {
            const toSaveAudios: Content[] = audios.map((d) => {
                if (d.nid === nid) {
                    audio = {
                        ...d,
                        played: timeStamp,
                    };
                    return audio;
                } else {
                    return d;
                }
            });
            setGlobalAudios(toSaveAudios);
        } else if (
            receivedMemberAudios?.filter((video) => video.nid === nid).length >
            0
        ) {
            const toSaveReceivedMembers = receivedMemberAudios.map((d) =>
                setPlayedTime(nid, d, timeStamp),
            );
            setGlobalGifts({
                receivedMemberAudios: sortArrayByKey(
                    toSaveReceivedMembers,
                    "title",
                    "ASC",
                ),
            });
        } else {
            const toSaveProspectsMembers = receivedProspectAudios.map((d) =>
                setPlayedTime(nid, d, timeStamp),
            );
            setGlobalGifts({
                receivedProspectAudios: sortArrayByKey(
                    toSaveProspectsMembers,
                    "title",
                    "ASC",
                ),
            });
        }

        updateRecentlyPlayed({ ...audio, played: timeStamp });
    };

    const handleBackNavigation = () => {
        navigate(-1);
    };

    const [isLoop, setLoop] = useState(false);

    const isAudiobook = useIsAudiobook(downloadedAudioDetail);

    const partDisclosure = audiobookPartDisclosure({
        isAudiobook,
        disclosure: audio?.disclosure,
        partIndex: mediaIndex,
        partCount: media?.length,
    });

    const disclosure = useDisclosureGate({
        disclosure: partDisclosure,
        skuId: audio?.skuId,
        nId: contentId?.split("-")[1],
        contentType: contentTypeOf(
            downloadedAudioDetail,
            isReceivedMemberAudio ? "GiftMP3" : "MP3",
        ),
    });

    // keyed the same way onMediaPlayerReady finds its resume row, so a resumed
    // part does not replay its opening disclosure
    const isResuming = useIsResumingBundle(
        audio?.id?.split("-")[1] ?? "",
        media?.[mediaIndex]?.media_title,
    );

    const sequence = useDisclosureSequence({
        contentUrl: mediaURL,
        disclosure: partDisclosure,
        startUrl: disclosure.startUrl,
        endUrl: disclosure.endUrl,
        resolving: disclosure.resolving,
        autoPlay: currentPlayingState,
        advanceOnComplete: mediaIndex < media?.length - 1,
        skipStart: isResuming,
        analytics: disclosure.analytics,
    });

    usePublishDisclosurePlaying(sequence.isDisclosure);

    const handleSequencePlay = () => {
        sequence.onPlay();
        if (!sequence.isDisclosure) handlePlayerPlay();
    };

    const handleSequencePause = () => {
        sequence.onPause();
        if (!sequence.isDisclosure) handlePlayerPause();
    };

    const handleSequenceStarted = () => {
        if (!sequence.isDisclosure) handleAudioStartedPlaying();
    };

    const handleSequenceEnded = () => {
        const contentJustEnded = !sequence.isDisclosure;
        const sequenceDone = sequence.handleEnded();

        if (contentJustEnded) completeCurrentPart();
        if (sequenceDone) advanceToNextPart();
    };

    return (
        <>
            {media && media[mediaIndex] && (
                <RootDiv {...rest} className={className}>
                    <AppHeader
                        title={media[mediaIndex].media_title}
                        canGoBack
                        searchable={false}
                        onBackClick={handleBackNavigation}
                    />
                    <Grid container>
                        {audio && (
                            <>
                                <Grid
                                    container
                                    xs={12}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    {isBundle ? (
                                        <>
                                            <Grid container>
                                                <Grid item xs={12}>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent={
                                                            "space-between"
                                                        }
                                                    >
                                                        <ForwardBackItemBox
                                                            display={"flex"}
                                                            alignItems="center"
                                                            onClick={previous}
                                                        >
                                                            <BackIcon
                                                                icon={[
                                                                    "fas",
                                                                    "chevron-left",
                                                                ]}
                                                            />
                                                            <FwdBackTypography
                                                                fontWeight={
                                                                    "bold"
                                                                }
                                                                variant="h5"
                                                            >
                                                                Previous
                                                            </FwdBackTypography>
                                                        </ForwardBackItemBox>
                                                        <ForwardBackItemBox
                                                            display={"flex"}
                                                            alignItems="center"
                                                            onClick={next}
                                                        >
                                                            <FwdBackTypography
                                                                fontWeight={
                                                                    "bold"
                                                                }
                                                                variant="h5"
                                                            >
                                                                Next
                                                            </FwdBackTypography>
                                                            <FwdIcon
                                                                icon={[
                                                                    "fas",
                                                                    "chevron-right",
                                                                ]}
                                                            />
                                                        </ForwardBackItemBox>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                            <PlayerContainerGrid item xs={12}>
                                                <MediaPlayer
                                                    title={
                                                        media[mediaIndex]
                                                            .media_title
                                                    }
                                                    playerRef={bundlePlayerRef}
                                                    progressInterval={
                                                        PLAYER_PROGRESS_INTERVAL
                                                    }
                                                    onProgress={
                                                        onPlayerProgress
                                                    }
                                                    onStart={
                                                        handleSequenceStarted
                                                    }
                                                    onReady={() =>
                                                        onMediaPlayerReady()
                                                    }
                                                    mediaURL={sequence.url}
                                                    restartToken={
                                                        sequence.stepToken
                                                    }
                                                    imageURL={FallbackWaveform}
                                                    onEnded={
                                                        handleSequenceEnded
                                                    }
                                                    loop={
                                                        sequence.isSequencing
                                                            ? false
                                                            : isLoop
                                                    }
                                                    isAudio={true}
                                                    onNext={next}
                                                    onPrevious={previous}
                                                    playing={
                                                        sequence.playing &&
                                                        !miniPlayer.url &&
                                                        !disclosure.blocked
                                                    }
                                                    isDisclosureSource={sequence.isDisclosure}

                                                    blockedMessage={
                                                        disclosure.blocked ||
                                                        sequence.clipFailed
                                                            ? disclosure.message
                                                            : undefined
                                                    }
                                                    onPlay={handleSequencePlay}
                                                    onPause={
                                                        handleSequencePause
                                                    }
                                                    onUpdateExpiredUrl={() => {
                                                        setIsReady(false);
                                                        setIsReadyToPlay(true);
                                                    }}
                                                    onError={() => {
                                                        setIsReadyToPlay(true);
                                                        sequence.onError();
                                                    }}
                                                    loading={!isReadyToPlay}
                                                />
                                            </PlayerContainerGrid>
                                            <Box
                                                display={"flex"}
                                                width={"100%"}
                                                justifyContent={"center"}
                                            >
                                                <PlayerActionBtns
                                                    handleChangeSeek={
                                                        handleChangeSeek
                                                    }
                                                    onClickLoop={() =>
                                                        setLoop(!isLoop)
                                                    }
                                                    isLoop={isLoop}
                                                />
                                            </Box>
                                        </>
                                    ) : (
                                        <DummyImageBox
                                            display={"flex"}
                                            alignItems={"center"}
                                            justifyContent={"flex-end"}
                                        >
                                            <GraphicWrapper>
                                                <GraphicIcon
                                                    sx={{
                                                        fontSize: "60px",
                                                    }}
                                                />
                                            </GraphicWrapper>
                                        </DummyImageBox>
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <Grey800Typography variant="body2">
                                        {audio.description}
                                    </Grey800Typography>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </RootDiv>
            )}
        </>
    );
};
